import { useState, useEffect, useCallback } from 'react';

interface NetworkStatus {
  isOnline: boolean;
  wasOffline: boolean;
  lastOnlineTime: Date | null;
}

/**
 * Hook to detect network connectivity status
 * Monitors browser online/offline events and provides network status
 */
export function useNetworkStatus() {
  const [networkStatus, setNetworkStatus] = useState<NetworkStatus>(() => {
    const isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
    return {
      isOnline,
      wasOffline: false,
      lastOnlineTime: isOnline ? new Date() : null,
    };
  });

  const checkConnection = useCallback(async (): Promise<boolean> => {
    try {
      // Try to fetch a small resource to verify actual connectivity
      // Using a cache-busting parameter to ensure we're not getting cached responses
      const response = await fetch('/api/health', {
        method: 'HEAD',
        cache: 'no-cache',
        signal: AbortSignal.timeout(3000), // 3 second timeout
      });
      return response.ok;
    } catch (error) {
      // If API check fails, we'll trust navigator.onLine instead
      // This prevents false positives when API is down but internet is fine
      return typeof navigator !== 'undefined' ? navigator.onLine : true;
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      // When browser reports online, trust it immediately
      // Optionally verify with API check, but don't override if API fails
      setNetworkStatus((prev) => {
        // Only update if we were previously offline
        if (!prev.isOnline) {
          return {
            isOnline: true,
            wasOffline: true,
            lastOnlineTime: new Date(),
          };
        }
        return prev;
      });
      
      // Optionally verify with API (but don't override if it fails)
      checkConnection().catch(() => {
        // Silently fail - we trust navigator.onLine
      });
    };

    const handleOffline = () => {
      // When browser reports offline, trust it immediately
      setNetworkStatus((prev) => ({
        isOnline: false,
        wasOffline: true,
        lastOnlineTime: prev.lastOnlineTime,
      }));
    };

    // Initial state - trust navigator.onLine
    if (typeof navigator !== 'undefined') {
      const initialOnline = navigator.onLine;
      setNetworkStatus({
        isOnline: initialOnline,
        wasOffline: false,
        lastOnlineTime: initialOnline ? new Date() : null,
      });
    }

    // Listen to browser online/offline events
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Periodic connectivity check (only when browser reports offline)
    const intervalId = setInterval(async () => {
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        // Only check API when browser says we're offline
        const apiOnline = await checkConnection();
        if (apiOnline && navigator.onLine) {
          // If API says online and browser now says online, update
          handleOnline();
        }
      }
    }, 30000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(intervalId);
    };
  }, [checkConnection]);

  return networkStatus;
}

